import { uniqueId } from 'lodash';
import { useQuery } from 'react-query';
import { authFetch } from '../../../CommonUtils/authFetch';
import { urlWithParams } from '../../../CommonUtils/urlWithParams';
import { useClientId } from '../../../_hooks/useClientId';
import { ACCOUNTS_QUERY_KEY } from '../AccountsConstants';
import { AssetClassEnum } from './useDefaultAssetAllocationQuery';

export const LOANS_AND_CREDIT_CARDS = 'Loans And Credit Cards';

export const AccountTypeCategories = [
  'Bank Accounts',
  LOANS_AND_CREDIT_CARDS,
  'Employer-Sponsored Accounts',
  'Investment Accounts',
  'Others',
];

export type AccountTypeCategoryDTO = typeof AccountTypeCategories[number];

export interface BeneficiaryDTO {
  Id: string;
  Name: string;
  Percentage: number;
  Type: number;
}

export interface AccountBeneficiariesDTO {
  Beneficiaries: BeneficiaryDTO[];
  LastBeneficiaryUpdate: string | null;
}

export interface AccountEarmarkDTO {
  Type: string;
  Tooltip: string;
}

export enum ContributionPeriodDTO {
  Monthly,
  Annually,
}

interface OrionPortfolioGroups {
  Id: number;
  Name: string;
}

export enum EmployerMatchTypeDTO {
  DontKnow,
  NoMatch,
  Percent50,
  Percent100,
  PercentOther,
  SafeHarbor1,
  SafeHarbor2,
  PercentageOfCompensation,
  NonElective,
  MultiTier,
}

export enum EmployerLimitTypeDTO {
  NoLimit,
  UpTo3Percent,
  UpTo6Percent,
  Other,
}

export enum ContributionUnitDTO {
  Dollar,
  Percentage,
}

export enum MortgageTypeEnum {
  Fixed30Y,
  Fixed15Y,
  Arm10Y,
  Arm5Y,
  Arm1Y,
  InterestOnly,
  Fixed10Y,
  Fixed20Y,
  Fixed25Y,
  Fixed40Y,
}

export enum TaxStatusDTO {
  PreTax,
  Roth,
  AfterTax,
  Mix,
}

export type AccountTaxStatusDTO = TaxStatusDTO; // All values
export type AllocationTaxStatusDTO =
  | TaxStatusDTO.PreTax
  | TaxStatusDTO.Roth
  | TaxStatusDTO.AfterTax; // Subset of TaxStatusDTO

export interface AccountHoldingDTO {
  AssetBucketIndex: number | null;
  AssetClasses: Record<AssetClassEnum, number>;
  Id: string;
  IsManuallyCreated: boolean;
  MarketValue: number;
  Name: string;
  Price: number;
  ProductId: number | null;
  Quantity: number;
  TaxStatus: AllocationTaxStatusDTO | null;
  Ticker: string;
}

interface VestingDTO {
  Id: string;
  VestingDate: string | null;
  VestedShares: number | null;
  SharesOwned: number;
  ExerciseDate: string | null;
  PricePerShare: number | null;
  ExpirationDate: string | null;
  GrantDate: string | null;
  DispositionStrategy: number | null;
  DispositionYear: string | null;
  DispositionDate: string | null;
  ExerciseStrategy: number | null;
  ExerciseYear: string | null;
  SharesToExercise: number;
}

export enum GranTypeDTO {
  Rsu,
  Iso,
  Nso,
}

export interface GrantDTO {
  Id: string;
  GrantType: GranTypeDTO;
  GrantTypeName: string;
  Value: number;
  TotalShares: number;
  GrantDate: string | null;
  Price: number;
  ExpectedReturn: number | null;
  VestingList: VestingDTO[];
}

export enum BalanceUnitDTO {
  Currency,
  Percentage,
}

export enum BalanceTaxStatusOptionDTO {
  PreTax = 'PreTax',
  Roth = 'Roth',
  AfterTax = 'AfterTax',
}

export type BalanceTaxStatusDTO = {
  Amount: number;
  Earmarks: AccountEarmarkDTO[];
  NonSpecificPositionsValue: number;
  AssetBucketIndex: number | null;
};

export enum ContributionTaxStatusDTO {
  Traditional,
  Roth,
  AfterTax,
}

export interface EmployerMultiTierMatchDTO<T> {
  Limit: T;
  Match: T;
}

export type ContributionsDTO = {
  EmployerEmployerLimit: number | null;
  EmployerLimitType: EmployerLimitTypeDTO | null;
  EmployerMatchAmount: number | null;
  EmployerMatchPercent: number | null;
  EmployerMatchType: EmployerMatchTypeDTO | null;
  EmployerMultiTierMatch: EmployerMultiTierMatchDTO<string>[] | null;
  EmployerTaxStatus: ContributionTaxStatusDTO;
  PersonalAmount: number;
  PersonalFrequency: ContributionPeriodDTO;
  PersonalMaximize: boolean;
  PersonalTaxStatus: ContributionTaxStatusDTO;
  PersonalUnit: BalanceUnitDTO;
};

export interface AnnuityLivingBenefitRiderDTO {
  CurrentBenefitBase: number;
  IncomeStartAtRetirement: boolean;
  IncomeStartYear: number;
  RollUpPeriod: number;
  RollUpRate: number;
  WithdrawalBenefit: number;
}

export interface DistributionStrategyDTO {
  DistributionPercentage?: number | null;
  DistributionDollarAmount?: number | null;
  ComputationYearType?: number | null;
  StartDistributionsToday?: boolean;
  StartDistributionsAtRetirement?: boolean;
  StartAfterYears?: number | null;
  EndDistributionsAtRetirement?: boolean;
  EndOfPlan?: boolean;
  EndAfterYears?: number | null;
  NoOtherDistributionsAllowed?: boolean;
}

export interface DAFScheduleDTO {
  StartYear: {
    Year: number | null;
    Type: number;
  };
  EndYear: {
    Year: number | null;
    Type: number;
  };
  Amount: number;
}

export enum OwnerTypeDTO {
  Principal = 0,
  Spouse = 1,
  Joint = 2,
  Trust = 3,
  Business = 4,
}

export interface OwnerModelDTO {
  Id: string | null;
  Name: string;
  OwnerType: OwnerTypeDTO;
  Percentage: number;
}

export interface ProductDTO {
  Id: number;
  Ticker: string;
  Price: number;
}

export interface AccountDTO {
  'AccountEarmark[Type]': string[];
  AccountBeneficiaries: AccountBeneficiariesDTO | null;
  AccountEarmark: AccountEarmarkDTO[] | null;
  AccountGrantType: any;
  AccountGrantTypeName: any;
  AccountId: string;
  AccountName: string;
  AccountOwner: string;
  AccountType: number;
  AccountTypeCategory: AccountTypeCategoryDTO;
  AdviserFees: any;
  AnnualReturnCap: number | null;
  AnnualReturnFloor: number | null;
  AnnuitizationDistributionDuration: number | null;
  AnnuitizationStartAtRetirement: boolean | null;
  AnnuitizationStartYear: number | null;
  AnnuityAnnualReturn: number | null;
  AnnuityDeathBenefitRiderAmount: number | null;
  AnnuityDistributionOption: number | null;
  AnnuityHasDeathBenefitRider: boolean | null;
  AnnuityHasLivingBenefitRider: boolean | null;
  AnnuityIndexName: string | null;
  AnnuityIsAlreadyAnnuitized: boolean | null;
  AnnuityIsAnnuitized: boolean | null;
  AnnuityIsQualified: boolean | null;
  AnnuityLivingBenefitRider: AnnuityLivingBenefitRiderDTO | null;
  AnnuityParticipationRate: number | null;
  AnnuityPretaxPrincipal: boolean | null;
  AnnuityPrincipal: number | null;
  AnnuityPurchaseYear: number | null;
  AnnuitySurrenderSchedule: string | null;
  AnnuitySurvivorBenefit: number | null;
  AnnuityType: number | null;
  AssociatedAssetId: string | null;
  CapitalGainsRatio: number | string | null;
  NonSpecificPositionsValue: number;
  SpecificPositionsValue: number;
  ClientId: string;
  Comments: any;
  ContributionEndYear: number | null;
  ContributionStartYear: number | null;
  CostBasis: string | number | null;
  CustomAssetBucketIndex: number | null;
  Description: string;
  DisabilityCoveragePeriod: any;
  DisabilityEliminationPeriod: any;
  DisabilityInsurancePolicyType: any;
  DisabilityMonthlyBenefitAmount: any;
  DistributionCalculationMethod: number;
  DistributionStrategy: DistributionStrategyDTO;
  DistributionStrategyType?: number;
  EmployerMatchUnits: ContributionUnitDTO;
  EmployerNonElectiveContribution: number;
  EmployerNonElectiveContributionTaxStatus: number;
  ExcludeFromDebtManagementGoal: boolean;
  ExcludeFromPlan: boolean;
  ExternalInstitutionId: any;
  ExternalInstitutionLinkId: any;
  GrantList: GrantDTO[];
  StockOptionProductDetails: ProductDTO | null;
  HasAsset: boolean;
  HasPositions: boolean;
  HealthCoinsurance: any;
  HealthCopayment: any;
  HealthFamilyDeductible: any;
  HealthIndividualDeductible: any;
  HealthInsuredMembers: any;
  HealthOutOfPocketLimit: any;
  HealthPlanType: number;
  InstitutionId: string;
  InstitutionLinkId: any;
  InstitutionName: string;
  InsuranceCoverageType: any;
  InsuranceExpirationYear: any;
  InsurancePremiumEndYear: any;
  InsuranceType: any;
  InterestRate: any;
  IraDistributionMethod: number;
  IraDistributionSchedule: any;
  IraDistributionStartYear: any;
  IsAsset: boolean;
  IsBrokerOfRecord: any;
  IsDeleted: boolean;
  IsIntegrated: boolean;
  IsLinkBroken: boolean;
  IsLinked: boolean;
  IsLiquid: boolean;
  IsMoveAllowed: boolean;
  IsOrion: boolean;
  IsOrionManagedAccount?: boolean;
  IsPreTaxPremium: any;
  IsSpouseSoleBeneficiary: any;
  LastUpdatedTimeStamp: string | null;
  LifeInsuranceGrowthRate: any;
  LifeInsurancePermanentType: any;
  LifeInsurancePolicyAmount: any;
  LifeInsurancePolicyStartYear: any;
  LifeInsurancePolicyTerm: any;
  LifeInsurancePolicyType: any;
  LongTermCareCoveragePeriod: any;
  LongTermCareDailyBenefitAmount: any;
  LongTermCareEliminationPeriod: any;
  LongTermCareHomeCareCoveragePercentage: any;
  LongTermCareNursingCareCoveragePercentage: any;
  MaximizeContributions: boolean;
  MonthlyContribution: any;
  MonthlyDistributionAmount: number | null;
  MonthlyPmi: number | null;
  MortgageType: MortgageTypeEnum | null;
  OriginationDate: string | null;
  OrionAccountId: any;
  OrionPortfolioGroups: OrionPortfolioGroups[];
  OwnerModel: OwnerModelDTO[];
  PercentageUsedInPlan: any;
  Premium: any;
  SelfEmployed: boolean;
  ShowAccount: boolean;
  ShowSyncMonthlyContributionsToggle: boolean;
  StockOptionAssetClass: any;
  SyncAccountValueEnabled: boolean;
  SyncAssetAllocationEnabled: boolean;
  SyncCostBasisEnabled: boolean;
  SyncMonthlyContributionEnabled: boolean;
  SyncPositionsEnabled: boolean;
  SyncSourceLabel: string | null;
  SyncTransactionsEnabled: boolean;
  TurnoverRatio: string | number | null;
  YearInherited: number | null;
  Contributions: ContributionsDTO[];
  Positions: AccountHoldingDTO[];
  IncludeInGrossEstate: boolean;
  HasAdvisorChangedBrokerOfRecord: boolean;
  TaxStatus: AccountTaxStatusDTO;
  Balances?: {
    PreTax?: BalanceTaxStatusDTO;
    Roth?: BalanceTaxStatusDTO;
    AfterTax?: BalanceTaxStatusDTO;
    Unit?: BalanceUnitDTO;
  };
  OrionManagementStyleId: number | null;
  FundingSourceAccountId: string | null;
  DAFContributionSchedule: DAFScheduleDTO;
  DAFGrantSchedule: DAFScheduleDTO;
  IsBypassRothIraIncomeLimits: boolean;
  StudentLoanOriginationDate: string | null;
  AccountNickname: string | null;
  GoalId: string | null;
}

interface AccountsDTO {
  Accounts: AccountDTO[];
}

const fetchAccounts = async (clientId: number): Promise<AccountsDTO> => {
  const response = await authFetch(
    urlWithParams('/Consumer/NewAccounts', {
      clientId,
    }),
  );

  return {
    Accounts: response.Accounts.map(account => ({
      ...account,
      Contributions: (account.Contributions ?? []).map(contribution => ({
        ...contribution,
        Id: uniqueId(),
        EmployerMultiTierMatch: contribution.EmployerMultiTierMatch
          ? contribution.EmployerMultiTierMatch.map(tier => ({ ...tier, Id: uniqueId() }))
          : null,
      })),
    })),
  };
};

export const ACCOUNT_PREFERENCES_QUERY_KEY = 'accountsPreferences';

export const useAccountsQuery = () => {
  const clientId = useClientId();

  return useQuery([ACCOUNTS_QUERY_KEY, ACCOUNT_PREFERENCES_QUERY_KEY, clientId], () =>
    fetchAccounts(clientId),
  );
};
